import { Icon } from "@iconify/react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useTicketApi } from "@/api/tickets";
import { RequestBoundary } from "@/components/request-boundary";
import { useI18n } from "@/locales/i18n-provider";
import { attachmentIcon, attachmentTypeLabel, formatFileSize, remoteAttachmentView, type TicketAttachmentView } from "../utils/ticket-attachments";
import { formatDateTime } from "../utils/ticket-presentation";
import { AttachmentPreviewDialog } from "./attachment-preview-dialog";

/**
 * 工单的全部附件。只列已随消息发出的那些——上传了却没发出去的草稿附件属于
 * 编辑器的中间状态，出现在这里会让人以为自己漏发了什么。
 */
export function TicketAttachmentList({ ticketId }: { ticketId: string }) {
  const { intlLocale, t } = useI18n();
  const ticketApi = useTicketApi();
  const [previewAttachment, setPreviewAttachment] = useState<TicketAttachmentView | null>(null);

  const query = useQuery({
    queryKey: ["tickets", "attachments", ticketId],
    queryFn: ({ signal }) => ticketApi.getAttachments(ticketId, signal),
  });

  const attachments = useMemo(
    () =>
      (query.data ?? [])
        .filter((attachment) => attachment.messageId !== null)
        .map((attachment) => ({ dto: attachment, view: remoteAttachmentView(attachment) })),
    [query.data],
  );

  // 预览对话框把它放进 effect 依赖里，引用必须稳定。
  const loadAttachmentContent = useCallback(
    (attachment: TicketAttachmentView) => {
      if (!attachment.remote) return Promise.reject(new Error(t("tickets.attachment.noRemoteSource")));
      return ticketApi.downloadAttachment(attachment.remote.ticketId, attachment.remote.attachmentId);
    },
    [t, ticketApi],
  );

  return (
    <RequestBoundary
      empty={{
        icon: <Icon className="size-5" icon="tabler:paperclip" />,
        title: t("tickets.attachment.emptyTitle"),
        description: t("tickets.attachment.emptyDescription"),
      }}
      isEmpty={(data) => data.every((attachment) => attachment.messageId === null)}
      loadingLabel={t("tickets.attachment.loading")}
      query={query}
    >
      <ul aria-label={t("tickets.attachment.ticketListAria")} className="mx-auto w-full max-w-3xl space-y-2 p-4">
        {attachments.map(({ dto, view }) => (
          <li key={view.key}>
            <button
              aria-label={t("tickets.attachment.openAria", { name: view.name })}
              className="group flex w-full items-center gap-3 rounded-lg border bg-muted/30 p-3 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              type="button"
              onClick={() => setPreviewAttachment(view)}
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-background text-muted-foreground ring-1 ring-border">
                <Icon className="size-5" icon={attachmentIcon(view)} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-foreground">{view.name}</span>
                <span className="block text-xs text-muted-foreground">
                  {attachmentTypeLabel(view, t)} · {formatFileSize(view.size, intlLocale)} · {formatDateTime(dto.creationTime, intlLocale)}
                </span>
              </span>
              <Icon className="size-4 shrink-0 text-muted-foreground group-hover:text-foreground" icon="tabler:download" />
            </button>
          </li>
        ))}
      </ul>
      <AttachmentPreviewDialog attachment={previewAttachment} loadContent={loadAttachmentContent} onClose={() => setPreviewAttachment(null)} />
    </RequestBoundary>
  );
}
